import { universalFetch } from "./common";
import { SelectedCitiesEnum } from "../selectedCities";
import type { DisplayPermissions } from "../interfaces/comparablesInterface";
import { mockListings } from "../mocks/newMockData/buyerExploreListingsData";
import type { PropertyTypeEnum } from "../interfaces/buyerMatchingPreferences";

const mapValue = (value: string) => (value === "5+" ? 5 : Number(value));

export const transformFilterValues = (values: string[] | undefined) =>
  values && values.length > 0
    ? values.every((value) => value === "Any")
      ? null
      : values.map(mapValue)
    : undefined;

export const normalizeToUndefined = <T>(
  value: T | null | undefined
): T | undefined => {
  return value === null ? undefined : value;
};

export const parseDaysOnMarket = (
  value: string | undefined | null
): DaysOnMarketFilterEnum | undefined => {
  switch (value) {
    case DaysOnMarketFilterEnum.ZeroToSevenDays:
    case DaysOnMarketFilterEnum.EightToFourteenDays:
    case DaysOnMarketFilterEnum.FifteenToThirtyDays:
    case DaysOnMarketFilterEnum.ThirtyPlusDays:
      return value;
    default:
      return undefined;
  }
};

export const mapStringToEnum = (cities: string[]): SelectedCitiesEnum[] => {
  return cities
    .map((city) => {
      const enumKey = Object.keys(SelectedCitiesEnum).find(
        (key) =>
          SelectedCitiesEnum[key as keyof typeof SelectedCitiesEnum] === city
      );
      return enumKey
        ? SelectedCitiesEnum[enumKey as keyof typeof SelectedCitiesEnum]
        : undefined;
    })
    .filter((city): city is SelectedCitiesEnum => city !== undefined);
};

export const parseBasement = (
  value: string | undefined | null
): BasementFilterEnum | undefined => {
  if (Object.values(BasementFilterEnum).includes(value as BasementFilterEnum)) {
    if (value !== "All") {
      return value as BasementFilterEnum;
    }
  }
  return undefined;
};

export const parseActiveListing = (
  value: string | undefined | null,
  togglesPropertyType: { active: boolean }
): ActiveListingsEnum | undefined | null => {
  if (togglesPropertyType.active) {
    if (value === "All") {
      return null;
    }
    if (
      value !== undefined &&
      value !== null &&
      Object.values(ActiveListingsEnum).includes(value as ActiveListingsEnum)
    ) {
      return value as ActiveListingsEnum;
    }
  }
  return undefined;
};

export const parseSoldListing = (
  value: string | undefined | null,
  togglesPropertyType: { sold: boolean }
): SoldListingsEnum | undefined | null => {
  if (togglesPropertyType.sold) {
    if (value === "All") {
      return null;
    }
    if (
      value !== undefined &&
      value !== null &&
      Object.values(SoldListingsEnum).includes(value as SoldListingsEnum)
    ) {
      return value as SoldListingsEnum;
    }
  }
  return undefined;
};

export const parseDeListedListing = (
  value: string | undefined | null,
  togglesPropertyType: { de_listed: boolean }
): DeListedListingsEnum | undefined | null => {
  if (togglesPropertyType.de_listed) {
    if (value === "All") {
      return null;
    }
    if (
      value !== undefined &&
      value !== null &&
      Object.values(DeListedListingsEnum).includes(
        value as DeListedListingsEnum
      )
    ) {
      return value as DeListedListingsEnum;
    }
  }
  return undefined;
};

export const parseOpenHouse = (
  value: string | undefined | null
): OpenHouseEnum | undefined => {
  if (Object.values(OpenHouseEnum).includes(value as OpenHouseEnum)) {
    if (value !== "All") {
      return value as OpenHouseEnum;
    }
  }
  return undefined;
};

export const parseNumKitchen = (
  value: string | undefined | null
): NumberOfKitchensFilterEnum | undefined => {
  if (
    Object.values(NumberOfKitchensFilterEnum).includes(
      value as NumberOfKitchensFilterEnum
    )
  ) {
    return value as NumberOfKitchensFilterEnum;
  }
  return undefined;
};
export const parseStyle = (
  value: string | undefined | null
): StyleFilterEnum | undefined => {
  if (Object.values(StyleFilterEnum).includes(value as StyleFilterEnum)) {
    return value as StyleFilterEnum;
  }
  return undefined;
};

export const parseGarage = (
  value: string | undefined | null
): GarageFilterEnum | undefined => {
  if (Object.values(GarageFilterEnum).includes(value as GarageFilterEnum)) {
    return value as GarageFilterEnum;
  }
  return undefined;
};

// interface SearchListingAddress {
// 	address1: string;
// 	address2: string;
// 	city: string;
// 	postalCode: string;
// 	state: string;
// 	country?: string;
// }
export interface SearchListing {
  page: number;
  listings: ListingCard[];
}

export interface ListingCard {
  listingId: string;
  displayImageUrl: string;
  propertyType: string | null;
  lastStatus: string | null;
  listDate: Date | null;
  listPrice: number | null;
  soldDate?: Date | null;
  soldPrice?: number | null;
  downPaymentBoost: number | null;
  minDownPayment: number | null;
  displayAddress: string | null;
  numBedrooms: number | null;
  numBathrooms: number | null;
  sqft: string | null;
  lat: number;
  long: number;
  permissions?: DisplayPermissions;
}

export enum DaysOnMarketFilterEnum {
  ZeroToSevenDays = "0-7_DAYS",
  EightToFourteenDays = "8-14_DAYS",
  FifteenToThirtyDays = "15-30_DAYS",
  ThirtyPlusDays = "30+_DAYS",
}

export enum AgeOfPropertyFilterEnum {
  ZeroToNineYears = "0_9_YEARS",
  TenToFortyYears = "10_40_YEARS",
  FortyPlusYears = "40_PLUS_YEARS",
}

export enum StyleFilterEnum {
  OneHalfStorey = "ONE_HALF_STOREY",
  TwoStorey = "TWO_STOREY",
  TwoHalfStorey = "TWO_HALF_STOREY",
  ThreeStorey = "THREE_STOREY",
  Apartment = "APARTMENT",
  BacksplitThree = "BACKSPLIT_THREE",
  BacksplitFour = "BACKSPLIT_FOUR",
  BacksplitFive = "BACKSPLIT_FIVE",
  Bungalow = "BUNGALOW",
  Bungaloft = "BUNGALOFT",
  Chalet = "CHALET",
  Contemporary = "CONTEMPORARY",
  GardenHouse = "GARDEN_HOUSE",
  Log = "LOG",
  Loft = "LOFT",
  Other = "OTHER",
  SidesplitThree = "SIDESPLIT_THREE",
  SidesplitFour = "SIDESPLIT_FOUR",
  SidesplitFive = "SIDESPLIT_FIVE",
}

export enum SquareFootageFilterEnum {
  LessThanFiveHundred = "LESS_THAN_500",
  FiveHundredToElevenHundred = "500_1100",
  ElevenHundredToFifteenHundred = "1100_1500",
  FifteenHundredPlus = "1500_PLUS",
}

export enum BasementFilterEnum {
  WalkOut = "WALK_OUT",
  SeparateEntrance = "SEPARATE_ENTRANCE",
  Finished = "FINISHED",
}

export enum ActiveListingsEnum {
  Within001Day = "WITHIN_001_DAY",
  Within003Days = "WITHIN_003_DAYS",
  Within007Days = "WITHIN_007_DAYS",
  Within030Days = "WITHIN_030_DAYS",
  Within090Days = "WITHIN_090_DAYS",
  MoreThan15Dom = "MORE_THAN_15_DOM",
  MoreThan30Dom = "MORE_THAN_30_DOM",
  MoreThan60Dom = "MORE_THAN_60_DOM",
  MoreThan90Dom = "MORE_THAN_90_DOM",
}

export enum SoldListingsEnum {
  Within001Day = "WITHIN_001_DAY",
  Within003Days = "WITHIN_003_DAYS",
  Within007Days = "WITHIN_007_DAYS",
  Within030Days = "WITHIN_030_DAYS",
  Within090Days = "WITHIN_090_DAYS",
  Within180Days = "WITHIN_180_DAYS",
  Within360Days = "WITHIN_360_DAYS",
  Year2024 = "YEAR_2024",
  Year2023 = "YEAR_2023",
  Year2022 = "YEAR_2022",
  Year2021 = "YEAR_2021",
  Year2020 = "YEAR_2020",
}

export enum DeListedListingsEnum {
  Within001Day = "WITHIN_001_DAY",
  Within003Days = "WITHIN_003_DAYS",
  Within007Days = "WITHIN_007_DAYS",
  Within030Days = "WITHIN_030_DAYS",
  Within090Days = "WITHIN_090_DAYS",
  Within180Days = "WITHIN_180_DAYS",
  Within360Days = "WITHIN_360_DAYS",
  Year2024 = "YEAR_2024",
  Year2023 = "YEAR_2023",
  Year2022 = "YEAR_2022",
  Year2021 = "YEAR_2021",
  Year2020 = "YEAR_2020",
}

export enum LastStatusEnum {
  Suspended = "Sus",
  Expired = "Exp",
  Sold = "Sld",
  Terminated = "Ter",
  DealFellThrough = "Dft",
  Leased = "Lsd",
  SoldConditionally = "Sc",
  SoldConditionallyWithEscapeClause = "Sce",
  LeasedConditionally = "Lc",
  PriceChange = "Pc",
  Extension = "Ext",
  New = "New",
  ComingSoon = "Cs",
}

export enum OpenHouseEnum {
  Today = "TODAY",
  Tomorrow = "TOMORROW",
  Days7 = "DAYS_7",
}

export enum ValencyEnum {
  Undervalued = "UNDERVALUED",
  Overvalued = "OVERVALUED",
  FairValue = "FAIR VALUE",
}

export enum GarageFilterEnum {
  Attached = "ATTACHED",
  Detached = "DETACHED",
}

export enum NumberOfKitchensFilterEnum {
  One = "ONE",
  Two = "TWO",
  ThreePlus = "THREE_PLUS",
}

export interface FiltersInterface {
  filters: ListingFiltersInterface;
}
export interface ListingFiltersInterface {
  propertyType?: PropertyTypeEnum[];
  active?: ActiveListingsEnum | null;
  sold?: SoldListingsEnum | null;
  delisted?: DeListedListingsEnum | null;
  numberOfBedrooms?: number[];
  numberOfBathrooms?: number[];
  numberOfParkingSpaces?: number[];
  dens?: boolean;
  keyword?: string;
  basement?: BasementFilterEnum;
  // openHouse?: OpenHouseEnum;
  priceRange?: number[];
  sqftRange?: number[];
  maintenanceFeeRange?: number[];
}

// export const fetchExploreListings = async (
// 	page?: string,
// 	limit?: string,
// 	sortBy?: string,
// 	sortOrder?: string,
// 	long?: number,
// 	lat?: number,
// 	zoom?: number,
// 	q?: string,
// 	filters?: ListingFiltersInterface,
// ): Promise<SearchListingInterface[]> => {
// 	try {
// 		// Simulate delay
// 		await new Promise((resolve) => setTimeout(resolve, 500));

// 		let filteredListings = [...mockListings];

// 		if (filters) {
// 			if (filters.numberOfBedrooms) {
// 				const bedrooms = transformFilterValues(
// 					filters.numberOfBedrooms.map(String),
// 				);
// 				if (bedrooms) {
// 					filteredListings = filteredListings.filter((listing) =>
// 						bedrooms.includes(listing.numBedrooms),
// 					);
// 				}
// 			}

// 			if (filters.numberOfParkingSpaces) {
// 				const parking = transformFilterValues(
// 					filters.numberOfParkingSpaces.map(String),
// 				);
// 				if (parking) {
// 					filteredListings = filteredListings.filter((listing) =>
// 						parking.includes(listing.numParkings),
// 					);
// 				}
// 			}

// 			if (filters.numberOfBathrooms) {
// 				const bathrooms = transformFilterValues(
// 					filters.numberOfBathrooms.map(String),
// 				);
// 				if (bathrooms) {
// 					filteredListings = filteredListings.filter((listing) =>
// 						bathrooms.includes(listing.numBathrooms),
// 					);
// 				}
// 			}

// 			if (filters.openHouse) {
// 				const openHouse = parseOpenHouse(filters.openHouse);

// 				if (openHouse) {
// 					filteredListings = filteredListings.filter(
// 						(listing) => listing.openHouse === openHouse,
// 					);
// 				}
// 			}

// 			if (filters.basement) {
// 				const basement = parseBasement(filters.basement);

// 				if (basement) {
// 					filteredListings = filteredListings.filter(
// 						(listing) => listing.basement === basement,
// 					);
// 				}
// 			}

// 			if (filters.propertyType) {
// 				filteredListings = filteredListings.filter((listing) =>
// 					filters.propertyType?.includes(listing.propertyType),
// 				);
// 			}
// 		}

// 		// Pagination - Limit and Page
// 		const pageNumber = page ? parseInt(page) : 1;
// 		const pageSize = limit ? parseInt(limit) : 10;
// 		const startIndex = (pageNumber - 1) * pageSize;
// 		const endIndex = startIndex + pageSize;

// 		// Return filtered listings with pagination
// 		return filteredListings.slice(startIndex, endIndex);
// 	} catch (error) {
// 		console.error("Failed fetchExploreListings", error);
// 		throw error;
// 	}
// };

export const fetchExploreListings = async (
  page?: string,
  limit?: string,
  sortBy?: string,
  sortOrder?: string,
  long?: number,
  lat?: number,
  zoom?: number,
  search?: "",
  filters?: ListingFiltersInterface
): Promise<ListingCard[]> => {
  try {
    const paramEntries: [string, string | number | undefined][] = [
      ["page", page],
      ["limit", limit],
      ["sortBy", sortBy],
      ["sortOrder", sortOrder],
      ["long", long],
      ["lat", lat],
      ["zoom", zoom],
      ["search", search],
    ];

    const params = new URLSearchParams(
      paramEntries.reduce((acc, [key, value]) => {
        if (value !== undefined) {
          acc[key] = value.toString();
        }
        return acc;
      }, {} as Record<string, string>)
    );

    const url = `/listings?${params.toString()}`;

    const requestBody = {
      url: url,
      filters: filters,
    };
    console.log("Request Body (JSON):", JSON.stringify(requestBody, null, 2));
    const response = await universalFetch(url, "POST", { filters });
    // const properties = response.listings as SearchListingInterface[];
    const properties = response.listings as ListingCard[];

    // const response = await universalFetch(url, "POST", { filters });
    // const properties = response.listings as SearchListingInterface[];
    return properties;
  } catch (error) {
    console.error("Failed fetchExploreListings", error);
    throw error;
  }
};
